// The shared shape of a permanent-delete confirmation: the thing being deleted named
// at the top, a list of what goes with it, the irreversibility said in red, and a
// danger button that stays put and reports the error when the delete is refused.
//
// Extracted when the retainer delete arrived, because the job and retainer dialogs
// differ ONLY in what they list — and the parts they share are the parts that must not
// drift: a warning that stopped naming the record, or a failed delete that closed the
// dialog as if it had worked, would be wrong on both screens at once.
//
// Rows with a count of zero are dropped by the caller. A list padded with "0 invoices,
// 0 files" reads as boilerplate and stops being read at all; what is left is what is
// real.
import { useState } from 'react';
import he from '../i18n/he';
import Modal from './Modal';
import Button from './Button';
import { TextInput } from './Form';
import AsyncBoundary, { ErrorText } from './Loader';
import { formatMoney } from './Money';
import { useReloadAlerts } from '../context/AlertsContext';

// One line of the destruction list: how many, of what, and — where it means something
// — how much money. A count of tasks or files has no amount, and inventing one would
// be noise.
export const DeletionLine = ({ count, label, amount }) => (
  <li style={{ marginBottom: 4 }}>
    <strong>{count}</strong> {label}
    {amount > 0 && <span className="muted"> · {formatMoney(amount)}</span>}
  </li>
);

// The delete is armed by TYPING THE RECORD'S NAME, not by a click alone.
//
// Added after a live retainer and its billing history were deleted by a click on the
// wrong screen. Naming the record in the dialog was not enough: the button sits where
// the eye already is, and a confirmation that costs one click is answered reflexively.
// Typing the title cannot be done by reflex — it forces the name to be read, and it is
// self-targeting, since typing THIS record's name proves you know which one is open.
//
// Compared on trimmed values: a stray trailing space in a job title (there is one in
// the live data) must not make a record undeletable.
// `confirmWord` overrides WHAT has to be typed, for the one case where the record's
// name is not the fact worth reading: a bulk delete, where the dialog heads a COUNT
// rather than a record and the number is the thing that can be wrong. Everything else
// about the guard is unchanged — it still cannot be answered by reflex, and typing the
// count still proves the selection was read.
const matches = (typed, name) => typed.trim() === String(name ?? '').trim();

export default function DeleteConfirmModal({
  title, name, subtitle, scopeNote, items = [], note, confirmWord,
  loading, error, confirmLabel, onConfirm, onClose, onDeleted, children,
}) {
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState(null);
  const [typed, setTyped] = useState('');
  const reloadAlerts = useReloadAlerts();
  const target = confirmWord ?? name;
  const armed = matches(typed, target);

  const confirm = async () => {
    if (!armed) return; // belt and braces; the button is disabled until it matches
    setBusy(true);
    setFailure(null);
    try {
      await onConfirm();
      // Whatever was deleted may have been what a sidebar dot was lit for — a late task,
      // a retainer month waiting to be invoiced — so the dots re-read now.
      reloadAlerts();
      onDeleted();
    } catch (e) {
      // A refused delete leaves everything intact — including the case where storage
      // could not be cleared, which stops before touching the database on purpose. So
      // the dialog stays open and can simply be retried.
      setFailure(e);
      setBusy(false);
    }
  };

  return (
    <Modal title={title} onClose={onClose}>
      <AsyncBoundary loading={loading} error={error}>
        {name && (
          <>
            <p>{he.common.deleteQuestion}</p>

            {/* Named at the top, so the confirmation cannot be given against the wrong
                record after a mis-click on the list behind it. */}
            <div className="named-record">
              <strong>{name}</strong>
              {subtitle && <div className="muted small-note">{subtitle}</div>}
              {scopeNote && <div className="muted small-note">{scopeNote}</div>}
            </div>

            {items.length > 0 ? (
              <>
                <p>{he.common.deleteWhatGoes}</p>
                <ul className="consequences">
                  {items.map((line) => <DeletionLine key={line.key} {...line} />)}
                </ul>
              </>
            ) : (
              <p className="muted">{he.common.deleteNothingAttached}</p>
            )}

            {children}

            <p className="danger-text">{he.common.deleteIrreversible}</p>
            {note && <p className="muted small-note">{note}</p>}

            {/* The name is shown again here rather than only at the top: it has to be
                copyable by eye from beside the field being typed into. */}
            <TextInput
              label={he.common.deleteTypeToConfirm}
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              disabled={busy}
              autoFocus
              placeholder={target}
            />
            <p className="field-note">
              {he.common.deleteTypeHint} <strong>{target}</strong>
            </p>

            {/* Red once armed: the irreversible action must not look like the cancel beside it. */}
            <div className="actions">
              <Button
                onClick={confirm}
                disabled={busy || !armed}
                variant={armed ? 'danger' : ''}
              >
                {busy ? he.common.deleting : (confirmLabel || he.common.deletePermanently)}
              </Button>
              <Button variant="subtle" onClick={onClose} disabled={busy}>{he.common.cancel}</Button>
            </div>

            {failure && <ErrorText error={failure} />}
          </>
        )}
      </AsyncBoundary>
    </Modal>
  );
}
